// Spec 47 approval-ux — cmp_approval_delegation_byline / delegation_byline.
//
// "Single-line attribution below a decision — or under the action bar
// before decide — indicating that the current actor is a delegate of
// another user. Reads 'Deciding as {{delegate}} on behalf of {{delegator}}
// • delegation expires {{when}}'." (spec 47 line 810)
//
// a11y per spec 47:
//   - Avatars carry aria-label with full names
//   - Byline read as a single sentence by screen reader
//   - Delegation link is a real <a> with focus ring + descriptive text

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface DelegationBylineProps extends HTMLAttributes<HTMLDivElement> {
  /** Name of the actor making the decision (the delegate). */
  readonly delegateName: string;
  /** Name of the user the delegation is on behalf of. */
  readonly delegatorName: string;
  /** Pre-formatted expiry string ("in 3d", "tomorrow", "Apr 28 2026"). */
  readonly expiresAt: string;
  /** Optional href to delegation detail (window, scope, reason). */
  readonly delegationHref?: string;
  /** Optional rendered avatars (already aria-labelled by caller). */
  readonly delegateAvatar?: ReactNode;
  readonly delegatorAvatar?: ReactNode;
  /** Override label for the delegation-detail link. */
  readonly viewDelegationLabel?: string;
}

export const DelegationByline = forwardRef<HTMLDivElement, DelegationBylineProps>(
  (
    {
      delegateName,
      delegatorName,
      expiresAt,
      delegationHref,
      delegateAvatar,
      delegatorAvatar,
      viewDelegationLabel = "View delegation",
      className,
      ...rest
    },
    ref,
  ) => {
    // Compose full sentence for screen readers (spec rule: byline read as
    // a single sentence, not fragmented at avatar boundaries).
    const ariaLabel = `Deciding as ${delegateName} on behalf of ${delegatorName}, delegation expires ${expiresAt}`;
    return (
      <div
        ref={ref}
        data-component="delegation-byline"
        aria-label={ariaLabel}
        className={cx("nai-delegation-byline", className)}
        {...rest}
      >
        <span data-component="delegation-byline-avatars" aria-hidden="true" className="nai-delegation-byline-avatars">
          {delegateAvatar}
          {delegatorAvatar}
        </span>
        <span data-component="delegation-byline-text" className="nai-delegation-byline-text">
          {`Deciding as ${delegateName} on behalf of ${delegatorName}`}
        </span>
        <span aria-hidden="true" className="nai-delegation-byline-separator">
          {" \u00b7 "}
        </span>
        <span data-component="delegation-byline-expiry" className="nai-delegation-byline-expiry">
          {`Delegation expires ${expiresAt}`}
        </span>
        {delegationHref !== undefined && (
          <>
            <span aria-hidden="true" className="nai-delegation-byline-separator">
              {" \u00b7 "}
            </span>
            <a
              href={delegationHref}
              data-component="delegation-byline-link"
              className="nai-delegation-byline-link"
            >
              {viewDelegationLabel}
            </a>
          </>
        )}
      </div>
    );
  },
);
DelegationByline.displayName = "DelegationByline";
